/**
 * When taskpane.html is opened in a browser (not inside Excel), show a VPN-gated
 * landing page with manifest downloads and sideload instructions — same probe
 * pattern as aia-rfp-ui (OPTIONS to gateway).
 */

import { getManifestDownloadPairs, MANIFEST_BASENAME_RE } from "../skins"

declare const __EXCEL_VPN_PROBE_URL__: string | undefined
declare const __EXCEL_BUILD_VERSION__: string | undefined

const DEFAULT_VPN_PROBE_URL = "https://gateway.dev.aiapollo.ai/api/query"

const SIDELOAD_DOCS =
	"https://learn.microsoft.com/office/dev/add-ins/testing/sideload-office-add-ins-for-testing"

export function getBuildVersion(): string {
	try {
		if (typeof __EXCEL_BUILD_VERSION__ === "string" && __EXCEL_BUILD_VERSION__.trim()) {
			return __EXCEL_BUILD_VERSION__.trim()
		}
	} catch {
		/* webpack define is unavailable in tests/browser preview */
	}
	return "local-dev"
}

export function getVpnProbeUrl(): string {
	let raw: string | undefined
	try {
		// Replaced with a string literal by webpack DefinePlugin in bundled builds
		raw = __EXCEL_VPN_PROBE_URL__
	} catch {
		raw = undefined
	}
	if (raw == null || String(raw).trim() === "") {
		const g = globalThis as { __EXCEL_VPN_PROBE_URL__?: string }
		if (typeof g.__EXCEL_VPN_PROBE_URL__ === "string" && g.__EXCEL_VPN_PROBE_URL__.trim()) {
			raw = g.__EXCEL_VPN_PROBE_URL__
		}
	}
	const out = raw && raw.trim() ? raw.trim() : DEFAULT_VPN_PROBE_URL
	return out
}

export async function probeVpn(url: string): Promise<boolean> {
	const controller = new AbortController()
	const timeout = window.setTimeout(() => controller.abort(), 5000)
	try {
		const res = await fetch(url, {
			method: "OPTIONS",
			signal: controller.signal,
		})
		return res.ok
	} catch {
		return false
	} finally {
		window.clearTimeout(timeout)
	}
}

function manifestPairs(): { filename: string; label: string }[] {
	return getManifestDownloadPairs()
}

function absUrl(path: string): string {
	const base = window.location.origin.replace(/\/$/, "")
	const p = path.startsWith("/") ? path : `/${path}`
	return `${base}${p}`
}

/**
 * Path with `?download=1` for direct links / curl (server sends Content-Disposition).
 * Chrome’s **download manager** often fails on self-signed `https://localhost` for these
 * navigational downloads (“Check internet connection”); prefer {@link downloadManifestAsBlob}
 * for the in-page Download button.
 */
export function manifestDownloadPath(filename: string): string {
	const base = filename.replace(/^\//, "")
	if (!MANIFEST_BASENAME_RE.test(base)) {
		throw new Error(`Invalid manifest filename: ${filename}`)
	}
	return `/${base}?download=1`
}

function manifestFetchPath(filename: string): string {
	const base = filename.replace(/^\//, "")
	if (!MANIFEST_BASENAME_RE.test(base)) {
		throw new Error(`Invalid manifest filename: ${filename}`)
	}
	return `/${base}`
}

/**
 * Same URL as “Open in new tab”, then save via Blob + object URL. Uses the tab’s TLS
 * trust (fetch) and avoids Chrome’s separate download pipeline for `https://localhost`,
 * which commonly errors with “Check internet connection” on dev certs.
 */
export async function downloadManifestAsBlob(filename: string): Promise<void> {
	const path = manifestFetchPath(filename)
	const res = await fetch(path, { credentials: "same-origin" })
	if (!res.ok) {
		throw new Error(`Could not load ${filename} (${res.status} ${res.statusText})`)
	}
	const blob = await res.blob()
	const objectUrl = URL.createObjectURL(blob)
	try {
		const a = document.createElement("a")
		a.href = objectUrl
		a.download = filename.replace(/^\//, "")
		a.rel = "noopener"
		a.style.display = "none"
		a.setAttribute("aria-hidden", "true")
		document.body.appendChild(a)
		a.click()
		a.remove()
	} finally {
		URL.revokeObjectURL(objectUrl)
	}
}

/**
 * Mount VPN check → blocked placeholder or manifest landing (never login).
 */
export function startBrowserLanding(root: HTMLElement): void {
	const probeUrl = getVpnProbeUrl()
	root.innerHTML = ""
	root.classList.add("excel-browser-landing-host")

	const shell = document.createElement("div")
	shell.className = "excel-browser-landing"
	root.appendChild(shell)

	let pollTimer: number | undefined
	let disposed = false

	const clearPoll = () => {
		if (pollTimer !== undefined) {
			window.clearInterval(pollTimer)
			pollTimer = undefined
		}
	}

	const dispose = () => {
		disposed = true
		clearPoll()
	}

	const runProbe = async (silent: boolean): Promise<boolean> => {
		if (!silent) {
			shell.innerHTML = ""
			const checking = document.createElement("div")
			checking.className = "excel-vpn-gate excel-vpn-gate--checking"
			checking.innerHTML = `
				<div class="excel-vpn-gate__card">
					<h1 class="excel-vpn-gate__title">Checking connection…</h1>
					<p class="excel-vpn-gate__copy">Verifying VPN / internal network access.</p>
					<p class="excel-vpn-gate__endpoint">Probe: ${escapeHtml(probeUrl)}</p>
				</div>
			`
			shell.appendChild(checking)
		}
		const ok = await probeVpn(probeUrl)
		if (disposed) return ok
		if (ok) {
			clearPoll()
			renderManifestLanding(shell)
			return true
		}
		if (!silent) {
			renderBlocked(shell, probeUrl, () => {
				void runProbe(false)
			})
			if (pollTimer === undefined) {
				pollTimer = window.setInterval(() => {
					void runProbe(true)
				}, 8000)
			}
		}
		return false
	}

	window.addEventListener(
		"beforeunload",
		() => {
			dispose()
		},
		{ once: true },
	)

	void runProbe(false)
}

function escapeHtml(s: string): string {
	return s
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
}

function renderBlocked(
	shell: HTMLElement,
	probeUrl: string,
	onRetry: () => void,
): void {
	shell.innerHTML = ""
	const gate = document.createElement("div")
	gate.className = "excel-vpn-gate"
	gate.innerHTML = `
		<div class="excel-vpn-gate__card">
			<h1 class="excel-vpn-gate__title">Connect to VPN to continue</h1>
			<p class="excel-vpn-gate__copy">
				This page is internal-only. Connect to VPN (or your corporate network), then retry to download manifests and sideload instructions.
			</p>
			<p class="excel-vpn-gate__endpoint">Gateway probe: ${escapeHtml(probeUrl)}</p>
			<div class="excel-vpn-gate__actions">
				<button type="button" class="btn btn--primary btn--md excel-vpn-gate__retry">Retry connection</button>
				<button type="button" class="btn btn--outline btn--md excel-vpn-gate__reload">Reload page</button>
			</div>
		</div>
	`
	shell.appendChild(gate)
	gate.querySelector(".excel-vpn-gate__retry")?.addEventListener("click", onRetry)
	gate.querySelector(".excel-vpn-gate__reload")?.addEventListener("click", () => {
		window.location.reload()
	})
}

function renderManifestLanding(shell: HTMLElement): void {
	shell.innerHTML = ""
	const page = document.createElement("div")
	page.className = "excel-manifest-landing excel-manifest-landing--compact"
	const buildVersion = getBuildVersion()

	const pairs = manifestPairs()
	const buttonsHtml = pairs
		.map(({ filename, label }) => {
			const href = absUrl(filename)
			return `<div class="excel-manifest-landing__action-row">
				<button type="button" class="btn btn--primary excel-manifest-landing__dl--xl" data-manifest-filename="${escapeHtml(filename)}">${escapeHtml(label)}</button>
				<a class="excel-manifest-landing__view-xml" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">View XML</a>
			</div>`
		})
		.join("")

	page.innerHTML = `
		<div class="excel-manifest-landing__card excel-manifest-landing__card--compact">
			<h1 class="excel-manifest-landing__title excel-manifest-landing__title--xl">Get the Excel add-in</h1>
			<p class="excel-manifest-landing__tagline">Download a manifest, then upload it in Excel.</p>
			<p class="excel-manifest-landing__dl-status" role="status" aria-live="polite" hidden></p>
			<div class="excel-manifest-landing__actions">${buttonsHtml}</div>
			<p class="excel-manifest-landing__one-liner"><strong>Excel:</strong> Insert → Add-ins → <strong>Upload My Add-in</strong> → choose the <code>.xml</code> file.</p>
			<p class="excel-manifest-landing__help-link"><a href="${SIDELOAD_DOCS}" target="_blank" rel="noopener noreferrer">Full sideload steps (Microsoft)</a></p>
			<p class="excel-manifest-landing__build">Build: <code>${escapeHtml(buildVersion)}</code></p>
		</div>
	`
	shell.appendChild(page)

	const statusEl = page.querySelector(".excel-manifest-landing__dl-status") as HTMLElement | null
	page.querySelectorAll<HTMLButtonElement>(".excel-manifest-landing__dl--xl").forEach((btn) => {
		const filename = btn.getAttribute("data-manifest-filename")
		if (!filename) return
		btn.addEventListener("click", () => {
			btn.disabled = true
			if (statusEl) {
				statusEl.hidden = false
				statusEl.textContent = `Saving ${filename}…`
			}
			void downloadManifestAsBlob(filename)
				.then(() => {
					if (statusEl) {
						statusEl.textContent = `Done — upload ${filename} in Excel.`
					}
				})
				.catch((err: unknown) => {
					const msg = err instanceof Error ? err.message : "Download failed."
					if (statusEl) {
						statusEl.textContent = `${msg} Use View XML → Save as.`
					}
				})
				.finally(() => {
					btn.disabled = false
				})
		})
	})
}
